export const normalizeModality = (value) => String(value || '').trim().toUpperCase();
export const modalityOf = (series) => normalizeModality(series?.modality || series?.Modality);
const descOf = (series) => [
  series?.seriesDescription,
  series?.SeriesDescription,
  series?.series_desc,
  series?.description,
  series?.protocolName,
  series?.protocol_name,
  series?.ProtocolName,
  series?.bodyPartExamined,
  series?.body_part_examined,
  series?.body_part,
  series?.BodyPartExamined,
].filter(Boolean).join(' ').toLowerCase();
export const instanceCountOf = (series) => Number(series?.numInstances || series?.num_instances || series?.instanceCount || series?.instances || 0) || 0;

const MPR_EXCLUDE = /\b(mip|minip|sbi|scout|localiz|dose\s*(report|screen|sr)|radiation\s*dose|cor\b|sag\b|coronal|sagittal|reformat|projection|spectral)/i;
const PET_EXCLUDE = /\b(mip|report|dose\s*(report|screen|sr)?|screen|snapshot|scout|localiz|radiation\s*dose)/i;

const hasToken = (series, tokenRegex) => tokenRegex.test(descOf(series));
const isPetModality = (series) => ['PT', 'PET'].includes(modalityOf(series));
const isWholeBody = (series) => hasToken(series, /\b(wb|whole\s*body|wholebody)\b/i);
const isDualDelay = (series) => hasToken(series, /\b(dual[-_\s]*delay|dual\s*time|delayed?|delay)\b/i);
const isExplicitLargeFovPet = (series) => isPetModality(series) && hasToken(series, /\b(?:[5-9]\d|1\d{2})\s*cm\b/i);
const PET_NAC = /\bnac\b|uncorr(?:ected)?|non[-_\s]*(?:atten|ac|corr)/i;
const isUncorrectedPet = (series) => hasToken(series, PET_NAC);
const isAttenuationCorrectedPet = (series) => hasToken(series, /(^|[^a-z])ac([^a-z]|$)|atten|corrected|\bcorr\b/i) && !isUncorrectedPet(series);

const isPetFusionAcquisitionBase = (series) => (
  isWholeBody(series)
  || isDualDelay(series)
  || hasToken(series, /\b(ct[-_\s]*ac|ctac|ac[-_\s]*ct|attenuation|atten\s*corr|low[-_\s]*dose|ldct)\b/i)
);

const SHARP_CT_KERNEL = /\bb(?:7\d|8\d)[a-z0-9]*\b/i;

const isLikelyDiagnosticCt = (series) => (
  hasToken(series, /\b(thorax|chest|lung|hrct|diagnostic|diag|b70[a-z0-9]*|bone|sharp|angio|cta|arterial|venous|portal)\b/i)
  && !isPetFusionAcquisitionBase(series)
);

export const eligibleMprBaseSeries = (seriesList = []) => seriesList
  .filter(s => !isPetModality(s))
  .filter(s => instanceCountOf(s) >= 20)
  .filter(s => !MPR_EXCLUDE.test(descOf(s)));

const eligiblePetFusionBaseSeries = (seriesList = []) => eligibleMprBaseSeries(seriesList)
  .filter(s => modalityOf(s) === 'CT');

export const eligiblePetFusionSeries = (seriesList = []) => seriesList
  .filter(s => isPetModality(s))
  .filter(s => instanceCountOf(s) >= 8)
  .filter(s => !PET_EXCLUDE.test(descOf(s)));

export const pickBestMprBaseSeries = (seriesList = []) => (
  eligibleMprBaseSeries(seriesList)
    .sort((a, b) => {
      const aCT = modalityOf(a) === 'CT' ? 1 : 0;
      const bCT = modalityOf(b) === 'CT' ? 1 : 0;
      if (aCT !== bCT) return bCT - aCT;
      return instanceCountOf(b) - instanceCountOf(a);
    })[0]
  || seriesList.filter(s => !isPetModality(s) && instanceCountOf(s) >= 20)[0]
  || seriesList[0]
);

export const scorePetFusionPair = (baseSeries, petSeries) => {
  if (!baseSeries || !petSeries) return Number.NEGATIVE_INFINITY;
  const ctCount = instanceCountOf(baseSeries);
  const petCount = instanceCountOf(petSeries);
  let score = 0;

  score -= Math.abs(ctCount - petCount) * 20;
  if (ctCount === petCount) score += 20000;
  if (ctCount > 0 && Math.abs(ctCount - petCount) <= Math.max(2, ctCount * 0.03)) score += 10000;

  if (isWholeBody(baseSeries) && isWholeBody(petSeries)) score += 5000;
  if (isDualDelay(baseSeries) && isDualDelay(petSeries)) score += 5000;
  if (isWholeBody(baseSeries) && isDualDelay(petSeries)) score -= 5000;
  if (isDualDelay(baseSeries) && isWholeBody(petSeries)) score -= 2500;
  if (isAttenuationCorrectedPet(petSeries)) score += 1000;
  // Some vendor PET/CT studies label the wide/whole-body PET by FOV (for example
  // "55CM") while the matching CTAC series is only distinguishable by broader
  // coverage/count. Boost explicit large-FOV PET so an exact-count short CT/PT pair
  // cannot beat the intended long-coverage PET fusion pair.
  if (isExplicitLargeFovPet(petSeries)) score += 35000;
  // When AC/corrected PET is paired with same-count NAC/uncorrected PET,
  // avoid the stable-sort/series-order tie that can default fusion to the
  // uncorrected volume. Keep the penalty strong enough to beat slice-count
  // closeness but below CT acquisition-family weights, so PET choice cannot
  // make an off-track CT base win.
  if (isUncorrectedPet(petSeries)) score -= 15000;

  // PET/CT studies often include a diagnostic CT with more slices than the
  // attenuation-correction/whole-body CT. Prefer CTAC/WB/dual-delay CTs for
  // fusion instead of letting raw slice count pick an off-track base.
  if (isPetFusionAcquisitionBase(baseSeries)) score += 50000;
  if (isWholeBody(baseSeries)) score += 25000;
  if (isDualDelay(baseSeries)) score += 20000;
  if (isLikelyDiagnosticCt(baseSeries)) score -= 25000;
  // PET/CT often has matching soft-tissue and sharp/lung CT reconstructions
  // with identical geometry/counts. Prefer the softer fusion base over B70/B80
  // style kernels when all acquisition-family signals otherwise tie.
  if (hasToken(baseSeries, SHARP_CT_KERNEL)) score -= 1000;

  score += Math.min(petCount, 2000);
  return score;
};

export const pickPetFusionPair = (seriesList = []) => {
  const bases = eligiblePetFusionBaseSeries(seriesList);
  const pets = eligiblePetFusionSeries(seriesList);
  if (!bases.length || !pets.length) return { baseSeries: null, petSeries: null };

  let best = null;
  bases.forEach(base => {
    pets.forEach(pet => {
      const score = scorePetFusionPair(base, pet);
      if (!best || score > best.score) best = { score, baseSeries: base, petSeries: pet };
    });
  });
  return best ? { baseSeries: best.baseSeries, petSeries: best.petSeries } : { baseSeries: null, petSeries: null };
};

export const pickBestPetForMprBase = (seriesList = [], baseSeries = null) => {
  const pets = eligiblePetFusionSeries(seriesList);
  if (!pets.length) return null;
  if (!baseSeries) {
    return pets
      .slice()
      .sort((a, b) => instanceCountOf(b) - instanceCountOf(a))[0];
  }

  return pets
    .slice()
    .sort((a, b) => scorePetFusionPair(baseSeries, b) - scorePetFusionPair(baseSeries, a))[0];
};

export const pickDefaultMprFusionPair = (seriesList = []) => {
  const pets = eligiblePetFusionSeries(seriesList);
  if (pets.length) {
    const fusionPair = pickPetFusionPair(seriesList);
    if (fusionPair.baseSeries && fusionPair.petSeries) return fusionPair;
  }
  const base = pickBestMprBaseSeries(seriesList) || null;
  return { baseSeries: base, petSeries: null };
};
